import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { Download, Info, Plus, Upload } from 'lucide-react'
import {
  EmptyState, ErrorState, FormDialog, ImportDialog, PageHeader, PermissionGate, StatusBadge,
  EmployeePicker,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { usePayElements } from '@/features/payroll'
import { downloadFile, toApiProblem } from '@/lib/api/client'
import { bulkPaths } from '@/lib/bulk'
import { useCompensationMutations, useSalaryRevisions } from '../api'
import { compaRatioVerdict, REVISION_STATUS_LABELS, REVISION_TYPE_LABELS } from '../types'
import type { RevisionStatus, RevisionType } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

const STATUS_TONE: Record<RevisionStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'info',
  REJECTED: 'danger',
  APPLIED: 'success',
  CANCELLED: 'neutral',
}

/**
 * Proposing a pay rise, and what became of it.
 *
 * <p>Approving and applying are separate buttons on purpose. A revision approved in March and
 * effective in April must not change anybody's pay until April, and a screen that does both at
 * once either pays it early or leaves somebody to remember not to.
 */
/**
 * Where the opening-salary import lives.
 *
 * <p>On this page rather than on Pay elements, which already has an Import for the elements
 * themselves: two Import buttons side by side, one for the company's pay configuration and one
 * for everybody's salary, is how the wrong file gets sent. This is the page about what people
 * are paid. The import is the direct, audited path for figures that were already true before
 * the system existed — the revisions below stay the path for a change somebody approves.
 */
const SALARY_IMPORT = '/v1/payroll/employee-salaries'

export function SalaryRevisionsPage() {
  useBreadcrumb()
  const revisions = useSalaryRevisions()
  const elements = usePayElements()
  const { propose, submit, approve, reject, applyRevision } = useCompensationMutations()

  const [draft, setDraft] = useState<{
    employeeId: string; revisionType: RevisionType; effectiveFrom: string
    elementId: string; currentAmount: string; proposedAmount: string; reason: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)

  async function act(run: () => Promise<unknown>, fallback: string) {
    setError(null)
    try {
      await run()
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? fallback)
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Salary revisions"
        subtitle="Proposed, approved and applied — three separate steps, with separate permissions."
        actions={
          <>
          <PermissionGate permission="payroll:read-all">
            <Button
              type="button" variant="outline"
              onClick={() => {
                void downloadFile(bulkPaths(SALARY_IMPORT).export, 'current-salaries.xlsx')
              }}
            >
              <Download className="size-4" aria-hidden="true" />
              Export salaries
            </Button>
          </PermissionGate>
          <PermissionGate permission="payroll:write">
            <Button type="button" variant="outline" onClick={() => setImporting(true)}>
              <Upload className="size-4" aria-hidden="true" />
              Import opening salaries
            </Button>
          </PermissionGate>
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                employeeId: '', revisionType: 'INCREMENT',
                effectiveFrom: todayLocal(),
                elementId: '', currentAmount: '', proposedAmount: '', reason: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            Propose a change
          </Button>
          </>
        }
      />

      <ImportDialog
        open={importing}
        onOpenChange={setImporting}
        what="opening salaries"
        prefix={SALARY_IMPORT}
        invalidateKey={['payroll', 'employees']}
      />

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}

      <Card>
        {revisions.isPending && <Skeleton className="m-4 h-24" />}
        {revisions.isError && (
          <ErrorState error={revisions.error} onRetry={() => void revisions.refetch()} />
        )}
        {revisions.isSuccess && revisions.data.length === 0 && (
          <EmptyState
            title="Nothing proposed"
            description="A revision records what somebody was paid, what is proposed, and the band midpoint it was judged against."
          />
        )}
        {revisions.isSuccess && revisions.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Person</th>
                  <th className="p-3 text-start font-medium">Kind</th>
                  <th className="p-3 text-start font-medium">Effective</th>
                  <th className="p-3 text-end font-medium">From</th>
                  <th className="p-3 text-end font-medium">To</th>
                  <th className="p-3 text-end font-medium">Rise</th>
                  <th className="p-3 text-start font-medium">Against the band</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {revisions.data.map((revision) => (
                  <tr key={revision.id} className="border-b border-slate-100">
                    <td className="p-3">{revision.employeeName ?? revision.employeeCode}</td>
                    <td className="p-3 text-slate-600">
                      {REVISION_TYPE_LABELS[revision.revisionType as RevisionType]
                        ?? revision.revisionType}
                    </td>
                    <td className="p-3">{revision.effectiveFrom}</td>
                    <td className="p-3 text-end text-slate-600">{revision.currentAmount ?? '—'}</td>
                    <td className="p-3 text-end font-medium">{revision.proposedAmount}</td>
                    <td className="p-3 text-end">
                      {revision.increasePercent == null ? '—' : `${revision.increasePercent}%`}
                    </td>
                    <td className="p-3 text-slate-600">
                      {revision.compaRatioAfter == null
                        ? <span className="text-slate-500">no band</span>
                        : `${revision.compaRatioAfter}% — ${compaRatioVerdict(revision.compaRatioAfter)}`}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={revision.status}
                        tone={STATUS_TONE[revision.status as RevisionStatus] ?? 'neutral'}
                        label={REVISION_STATUS_LABELS[revision.status as RevisionStatus]
                          ?? revision.status}
                      />
                    </td>
                    <td className="p-3 text-end">
                      <span className="flex justify-end gap-1">
                        {revision.status === 'DRAFT' && (
                          <Button
                            type="button" variant="outline" size="sm"
                            onClick={() => void act(
                              () => submit.mutateAsync(revision.id), 'It could not be submitted')}
                          >
                            Submit
                          </Button>
                        )}
                        {revision.status === 'SUBMITTED' && (
                          <>
                            <Button
                              type="button" variant="outline" size="sm"
                              onClick={() => void act(
                                () => approve.mutateAsync({ id: revision.id }),
                                'It could not be approved')}
                            >
                              Approve
                            </Button>
                            <Button
                              type="button" variant="outline" size="sm"
                              onClick={() => void act(
                                () => reject.mutateAsync({ id: revision.id }),
                                'It could not be rejected')}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {revision.status === 'APPROVED' && (
                          <Button
                            type="button" size="sm"
                            onClick={() => void act(
                              () => applyRevision.mutateAsync(revision.id),
                              'It could not be applied')}
                          >
                            Apply to pay
                          </Button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        You cannot approve a revision you proposed. Applying is a separate step from approving,
        so a rise approved in March and effective in April does not reach payroll until April.
      </p>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        size="lg"
        title="Propose a change to pay"
        description="The band midpoint in force is recorded with it, so the judgement stays explicable after the band moves."
        submitLabel="Save as draft"
        loading={propose.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void act(async () => {
            await propose.mutateAsync({
              employeeId: draft.employeeId,
              revisionType: draft.revisionType,
              effectiveFrom: draft.effectiveFrom,
              elementId: draft.elementId || null,
              currentAmount: draft.currentAmount ? Number(draft.currentAmount) : null,
              proposedAmount: Number(draft.proposedAmount),
              reason: draft.reason || null,
            })
            setDraft(null)
          }, 'That revision could not be saved')
        }}
      >
        {draft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="rev-employee">Person</Label>
                <EmployeePicker
                  id="rev-employee"
                  value={draft.employeeId}
                  onChange={(employeeId) => setDraft({ ...draft, employeeId: employeeId ?? '' })}
                />
              </div>
              <div>
                <Label htmlFor="rev-type">Kind</Label>
                <Select
                  id="rev-type"
                  className="w-full"
                  value={draft.revisionType}
                  onChange={(event) =>
                    setDraft({ ...draft, revisionType: event.target.value as RevisionType })}
                >
                  {Object.entries(REVISION_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="rev-element">Which pay element</Label>
                <Select
                  id="rev-element"
                  className="w-full"
                  value={draft.elementId}
                  onChange={(event) => setDraft({ ...draft, elementId: event.target.value })}
                >
                  <option value="">Choose an element…</option>
                  {elements.data?.map((element) => (
                    <option key={element.id} value={element.id}>
                      {element.name} ({element.code})
                    </option>
                  ))}
                </Select>
                <p className="mt-1 text-xs text-slate-600">
                  Needed before it can be applied — it is where the money is written.
                </p>
              </div>
              <div>
                <Label htmlFor="rev-from">Effective from</Label>
                <DatePicker
                  id="rev-from" value={draft.effectiveFrom}
                  onChange={(event) => setDraft({ ...draft, effectiveFrom: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="rev-current">Currently</Label>
                <Input
                  id="rev-current" type="number" value={draft.currentAmount}
                  onChange={(event) => setDraft({ ...draft, currentAmount: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="rev-proposed">Proposed</Label>
                <Input
                  id="rev-proposed" type="number" value={draft.proposedAmount}
                  onChange={(event) => setDraft({ ...draft, proposedAmount: event.target.value })}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="rev-reason">Why</Label>
              <Input
                id="rev-reason" value={draft.reason}
                placeholder="Annual review, consistently above expectations"
                onChange={(event) => setDraft({ ...draft, reason: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
